import {
  RoadSegment,
  BuildingBox,
  NeighborhoodTree,
  WasteBasket,
  SurveillanceCamera,
  InspectorNPC,
  TeaStallEntity,
  ChiliTrap,
  DummyWalkerNPC,
  MapId,
  MapOption,
} from '../domain/types.ts';

export const MAP_CATALOG: MapOption[] = [
  {
    id: 'cau-giay',
    name: 'Ngõ 165 Cầu Giấy',
    tagline: 'Ngõ Bàn Cờ — Quán Trà Đá 360° & Bác Tổ Trưởng',
    difficulty: '⭐⭐',
    features: ['Quán trà đá Cô Hoa', 'Tiệm vàng Camera 4K', 'Bẫy ớt bột Nhà Số 12'],
  },
  {
    id: 'ta-hien',
    name: 'Phố Cổ Tạ Hiện',
    tagline: 'Ngõ Bia Hơi Hẹp — Khách Đi Bộ Đông Đúc',
    difficulty: '⭐⭐⭐⭐',
    features: ['Khách bộ hành dày đặc', 'Chicken Game liên tục', '2 Camera Phố Đi Bộ'],
  },
  {
    id: 'linh-dam',
    name: 'Bán Đảo Linh Đàm',
    tagline: 'Quanh Cụm Chung Cư HH — Đấu Trường Chó Cưng',
    difficulty: '⭐⭐⭐',
    features: ['Công viên ven hồ', 'Rất nhiều Dummy Walker', 'Ban Quản Trị tuần tra'],
  },
];

export function createNeighborhoodMap(mapId: MapId = 'cau-giay') {
  // Đường sá: Ngõ 165 Cầu Giấy và các ngách
  const roads: RoadSegment[] = [
    // Trục đường chính (Phố Cầu Giấy ven ngõ)
    {
      id: 'road-main',
      name: 'Ngõ 165 Cầu Giấy (Trục chính)',
      width: 10,
      points: [
        { x: -70, y: 0 },
        { x: 70, y: 0 },
      ],
    },
    // Ngách 28 (Ngõ rẽ lên phía Bắc)
    {
      id: 'road-ngach-north',
      name: 'Ngách 28 (Hẻm tiệm vàng & nhà tổ trưởng)',
      width: 6,
      points: [
        { x: -20, y: -45 },
        { x: -20, y: 35 },
      ],
    },
    // Ngách cụt phía Nam (Nơi tưởng an toàn)
    {
      id: 'road-ngach-south',
      name: 'Ngách 12 (Ngõ cụt vắng vẻ)',
      width: 5,
      points: [
        { x: 30, y: -30 },
        { x: 30, y: 45 },
      ],
    },
  ];

  // Nhà dân & Tòa nhà
  const buildings: BuildingBox[] = [
    // Tiệm vàng Kim Tài (Gắn camera 4K nhìn ra ngã tư)
    {
      id: 'bldg-kimtai',
      name: 'Tiệm Vàng Kim Tài (Camera 4K)',
      x: -38,
      y: -28,
      width: 14,
      height: 18,
      hasCamera: true,
      ownerName: 'Ông Kim',
    },
    // Nhà Bác Tổ Trưởng Dân Phố (Tầng 2 hay nhìn xuống)
    {
      id: 'bldg-totruong',
      name: 'Nhà Bác Tổ Trưởng',
      x: -8,
      y: -30,
      width: 12,
      height: 16,
      hasCamera: false,
      ownerName: 'Bác Bình Tổ Trưởng',
    },
    // Tạp Hóa Chị Loan
    {
      id: 'bldg-taphoa',
      name: 'Tạp Hóa Chị Loan',
      x: -55,
      y: 8,
      width: 16,
      height: 14,
      hasCamera: false,
      ownerName: 'Chị Loan',
    },
    // Chung Cư Mini 7 Tầng
    {
      id: 'bldg-ccmini',
      name: 'Chung Cư Mini 7 Tầng',
      x: -8,
      y: 12,
      width: 20,
      height: 24,
      hasCamera: true,
      ownerName: 'Ban Quản Trị CCMini',
    },
    // Nhà số 12 (Chủ nhà cay cú rải ớt bột)
    {
      id: 'bldg-so12',
      name: 'Nhà Số 12 (Bẫy ớt bột)',
      x: 14,
      y: -28,
      width: 12,
      height: 16,
      ownerName: 'Bác Tư Khó Tính',
    },
    // Nhà nuôi 3 con chó hoang (Mục tiêu sút phân đổ tội)
    {
      id: 'bldg-chohoang',
      name: 'Nhà Nuôi 3 Chó Hoang (Mục tiêu đổ tội)',
      x: 38,
      y: -26,
      width: 16,
      height: 16,
      ownerName: 'Nhà Cô Mùi (Nuôi chó)',
    },
    // Cơm Bình Dân Đầu Hẻm
    {
      id: 'bldg-combinhdan',
      name: 'Cơm Bình Dân Đầu Hẻm',
      x: 18,
      y: 14,
      width: 18,
      height: 16,
      ownerName: 'Anh Hùng',
    },
    // Khối nhà mờ bên trái (như mẫu cũ)
    {
      id: 'ambient-bldg-left',
      name: 'Nhà dân mờ',
      x: -58,
      y: -26,
      width: 15,
      height: 16,
      isAmbient: true,
    },
    // Khối nhà mờ bên phải (như mẫu cũ)
    {
      id: 'ambient-bldg-right',
      name: 'Nhà dân mờ',
      x: 38,
      y: 14,
      width: 16,
      height: 14,
      isAmbient: true,
    },
  ];

  // Cây cối ven vỉa hè (Điểm chó thích ngửi và giải quyết)
  const trees: NeighborhoodTree[] = [
    { id: 'tree-1', x: -30, y: -4 },
    { id: 'tree-2', x: -6, y: -4 },
    { id: 'tree-3', x: 25, y: -4 },
    { id: 'tree-4', x: 45, y: -4 },
    { id: 'tree-5', x: -25, y: 18 },
    { id: 'tree-6', x: 26, y: 28 },
    { id: 'tree-7', x: 34, y: -16 },
  ];

  // Thùng rác công cộng (Điểm dọn phân hợp lệ)
  const wasteBaskets: WasteBasket[] = [
    { id: 'wb-1', x: -62, y: -4 },
    { id: 'wb-2', x: 55, y: -4 },
  ];

  // Camera An Ninh Cố Định
  const cameras: SurveillanceCamera[] = [
    // Camera Tiệm Vàng: Quét góc ngã ba
    {
      id: 'cam-kimtai',
      buildingName: 'Tiệm Vàng Kim Tài',
      x: -24,
      y: -10,
      facingAngle: Math.PI * 0.45, // Quét xuống mặt đường
      fov: (70 * Math.PI) / 180,
      range: 16,
      isBlinking: true,
    },
    // Camera Chung Cư Mini: Quét ngõ phía Đông
    {
      id: 'cam-ccmini',
      buildingName: 'Chung Cư Mini',
      x: 12,
      y: 12,
      facingAngle: -Math.PI * 0.35, // Quét chéo lên ngã tư
      fov: (75 * Math.PI) / 180,
      range: 17,
      isBlinking: true,
    },
  ];

  // NPC Bác Tổ Trưởng Tuần Tra (Cầm chổi quét sân)
  const inspectors: InspectorNPC[] = [
    {
      id: 'npc-totruong',
      name: 'Bác Bình Tổ Trưởng (Cầm chổi quét sân)',
      x: -16,
      y: -12,
      patrolPoints: [
        { x: -16, y: -12 },
        { x: -16, y: 15 },
        { x: 5, y: 15 },
        { x: 5, y: -12 },
      ],
      currentPointIndex: 0,
      speed: 1.4,
      facingAngle: Math.PI / 2,
      fov: (90 * Math.PI) / 180,
      range: 14,
      sweepAngle: 0,
    },
  ];

  // Quán Trà Đá Cô Hoa & Bàn Cờ Tướng (Radar 360 độ chạy bằng cơm!)
  const teaStalls: TeaStallEntity[] = [
    {
      id: 'teastall-cohoa',
      name: 'Quán Trà Đá Cô Hoa & Bàn Cờ Tướng',
      x: -45,
      y: -6,
      radius: 20, // 20m bán kính radar người
      activeComment: undefined,
      commentTimer: 0,
      cooldownTimer: 3000,
    },
  ];

  // Bẫy Ớt Bột (Nhà số 12 rắc trả đũa do hôm qua bị dính bãi phân)
  const chiliTraps: ChiliTrap[] = [
    {
      id: 'chili-so12',
      x: 14,
      y: -12,
      radius: 3.5,
    },
  ];

  return {
    roads,
    buildings,
    trees,
    wasteBaskets,
    cameras,
    inspectors,
    teaStalls,
    chiliTraps,
  };
}

export function createInitialDummyWalkers(mapId: MapId = 'cau-giay'): DummyWalkerNPC[] {
  return [
    // 1. Bà Tám / Thánh Mẫu (Tri hô, rọi đèn pin)
    {
      id: 'walker-batam',
      name: 'Bà Bảy Quán Cơm (Bà Tám)',
      archetype: 'ba_tam',
      x: -20,
      y: 10,
      facingAngle: Math.PI / 2,
      speed: 3.0,
      hasFlashlight: true,
      patrolRoute: [
        { x: -20, y: 30 },
        { x: -20, y: 0 },
        { x: 10, y: 0 },
        { x: -20, y: 0 },
      ],
      currentRouteIndex: 0,
      isCleaning: false,
      cleanTimer: 0,
      isFleeing: false,
      fleeTimer: 0,
      hasWitnessedPlayerDefect: false,
      isSeekingRevenge: false,
      inChickenGameWithPlayer: false,
    },

    // 2. Gen Z TikToker (Đưa máy quay 4K bóc phốt)
    {
      id: 'walker-genz',
      name: 'Bé Ly TikToker (100k Tim)',
      archetype: 'gen_z',
      x: 0,
      y: 0,
      facingAngle: 0,
      speed: 3.6,
      hasPhone: true,
      patrolRoute: [
        { x: -30, y: 0 },
        { x: 40, y: 0 },
        { x: 30, y: 20 },
        { x: 30, y: 0 },
      ],
      currentRouteIndex: 0,
      isCleaning: false,
      cleanTimer: 0,
      isFleeing: false,
      fleeTimer: 0,
      hasWitnessedPlayerDefect: false,
      isSeekingRevenge: false,
      inChickenGameWithPlayer: false,
    },

    // 3. Dân Phòng Ngõ Phố (Tuần tra, rọi đèn, lập biên bản)
    {
      id: 'walker-danphong',
      name: 'Anh Tuấn Dân Phòng',
      archetype: 'dan_phong',
      x: 30,
      y: -25,
      facingAngle: Math.PI / 2,
      speed: 4.0,
      hasFlashlight: true,
      patrolRoute: [
        { x: 30, y: -25 },
        { x: 30, y: 30 },
        { x: 30, y: 0 },
        { x: -10, y: 0 },
      ],
      currentRouteIndex: 0,
      isCleaning: false,
      cleanTimer: 0,
      isFleeing: false,
      fleeTimer: 0,
      hasWitnessedPlayerDefect: false,
      isSeekingRevenge: false,
      inChickenGameWithPlayer: false,
    },

    // 4. Bác Hưu Trí / Cụ Ông
    {
      id: 'walker-bachuutri',
      name: 'Cụ Hưng (Cán bộ hưu trí)',
      archetype: 'bac_huu_tri',
      x: -20,
      y: -20,
      facingAngle: -Math.PI / 2,
      speed: 2.2,
      patrolRoute: [
        { x: -20, y: -40 },
        { x: -20, y: 0 },
        { x: -50, y: 0 },
        { x: -20, y: 0 },
      ],
      currentRouteIndex: 0,
      isCleaning: false,
      cleanTimer: 0,
      isFleeing: false,
      fleeTimer: 0,
      hasWitnessedPlayerDefect: false,
      isSeekingRevenge: false,
      inChickenGameWithPlayer: false,
    },

    // 5. Thánh Mẫu Đạo Đức
    {
      id: 'walker-altruist',
      name: 'Bảo Ngọc (Thánh Mẫu)',
      archetype: 'altruist',
      x: 15,
      y: 0,
      facingAngle: 0,
      speed: 3.8,
      patrolRoute: [
        { x: 15, y: 0 },
        { x: 55, y: 0 },
        { x: 30, y: 25 },
        { x: 30, y: -20 },
        { x: -5, y: 0 },
      ],
      currentRouteIndex: 0,
      dog: {
        x: 13,
        y: 0,
        facingAngle: 0,
        bowels: 25,
        isPooping: false,
        poopTimer: 0,
        breadcrumbs: [],
      },
      isCleaning: false,
      cleanTimer: 0,
      isFleeing: false,
      fleeTimer: 0,
      hasWitnessedPlayerDefect: false,
      isSeekingRevenge: false,
      inChickenGameWithPlayer: false,
    },

    // 6. Chuyên Gia Lén Lút (Always Defect)
    {
      id: 'walker-defector',
      name: 'Tuấn Lươn (Lén Lút)',
      archetype: 'defector',
      x: -45,
      y: 0,
      facingAngle: 0,
      speed: 4.8,
      patrolRoute: [
        { x: -60, y: 0 },
        { x: -20, y: 0 },
        { x: -20, y: 25 },
        { x: -20, y: 0 },
      ],
      currentRouteIndex: 0,
      dog: {
        x: -47,
        y: 0,
        facingAngle: 0,
        bowels: 65,
        isPooping: false,
        poopTimer: 0,
        breadcrumbs: [],
      },
      isCleaning: false,
      cleanTimer: 0,
      isFleeing: false,
      fleeTimer: 0,
      hasWitnessedPlayerDefect: false,
      isSeekingRevenge: false,
      inChickenGameWithPlayer: false,
    },

    // 7. Ăn Miếng Trả Miếng (Tit-for-Tat vỉa hè)
    {
      id: 'walker-titfortat',
      name: 'Hùng Còi (Tit-for-Tat)',
      archetype: 'tit_for_tat',
      x: 30,
      y: 20,
      facingAngle: -Math.PI / 2,
      speed: 4.0,
      patrolRoute: [
        { x: 30, y: 35 },
        { x: 30, y: -20 },
        { x: 0, y: 0 },
        { x: 30, y: 0 },
      ],
      currentRouteIndex: 0,
      dog: {
        x: 30,
        y: 18,
        facingAngle: -Math.PI / 2,
        bowels: 40,
        isPooping: false,
        poopTimer: 0,
        breadcrumbs: [],
      },
      isCleaning: false,
      cleanTimer: 0,
      isFleeing: false,
      fleeTimer: 0,
      hasWitnessedPlayerDefect: false,
      isSeekingRevenge: false,
      inChickenGameWithPlayer: false,
    },
  ];
}

